import React, { useState, useEffect, useRef } from 'react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckCircle2,
  FileText,
  ChevronRight,
  Clock,
  Loader2,
  ClipboardList,
  AlertTriangle,
  Trash2,
} from 'lucide-react';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  validationService,
  type PendingValidation,
  type ValidationStats,
} from '@/services/kbService';
import DocumentDetailModal from '@/components/knowledge/DocumentDetailModal';
import { formatFileSize, formatRelativeTime, formatFullDate } from '@/utils/kbUtils';
import KBPageHeader from '@/components/knowledge/KBPageHeader';
import { StatsCardGrid, StatCardWithHeader } from '@/components/knowledge/StatCard';
import DataTablePagination from '@/components/knowledge/DataTablePagination';
import SortableTableHeader, { useSortableTable } from '@/components/knowledge/SortableTableHeader';
import RefreshButton from '@/components/knowledge/RefreshButton';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useKBFilters } from '@/hooks/useKBFilters';
import KBSearchAndFilters from '@/components/knowledge/KBSearchAndFilters';

import { logger } from '@/utils/logger';
interface QueueSummary {
  total_pending: number;
  queue_status: 'normal' | 'high' | 'critical';
  average_wait_time_minutes?: number;
}

type SortColumn = 'entity_count' | 'submitted_at';

const KBReviewQueuePage: React.FC = () => {
  const isMobile = useIsMobile();
  const filters = useKBFilters();

  // Data state
  const [documents, setDocuments] = useState<PendingValidation[]>([]);
  const [stats, setStats] = useState<ValidationStats | null>(null);
  const [queueSummary, setQueueSummary] = useState<QueueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const pageSize = 25;

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Monotonic id of the newest in-flight loadDocuments() call.
  const latestRequestRef = useRef(0);
  const [bulkApproving, setBulkApproving] = useState(false);

  // Modal state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // Sorting state
  const { sortColumn, sortDirection, handleSort } = useSortableTable<SortColumn>();

  // Auto-refresh for documents still being processed
  const [autoRefreshEnabled] = useState(true);

  // Failed ingestions - documents that died before a validation request was
  // created, so they never reach the pending table. Surfaced with their
  // error; removable so a clean re-upload is possible.
  interface FailedIngestion {
    name: string;
    file_name?: string;
    title?: string;
    file_size?: number;
    creation?: string;
    error_summary?: string;
  }
  const [failedDocs, setFailedDocs] = useState<FailedIngestion[]>([]);
  const [failedDeleteTarget, setFailedDeleteTarget] = useState<FailedIngestion | null>(null);
  const [failedDeleting, setFailedDeleting] = useState(false);

  // Failed Ingestions is paginated client-side - the endpoint returns the whole
  // list, and a failed batch can easily run to dozens of rows.
  const FAILED_PAGE_SIZE = 10;
  const [failedPage, setFailedPage] = useState(1);

  const loadFailedDocs = async () => {
    try {
      const res = await axios.get(getApiEndpoint('LIST_FAILED_DOCUMENTS'), { withCredentials: true });
      const data = res.data?.message;
      if (Array.isArray(data?.documents)) setFailedDocs(data.documents);
    } catch {
      /* panel simply stays hidden */
    }
  };
  useEffect(() => {
    loadFailedDocs();
  }, []);

  const handleDeleteFailed = async () => {
    if (!failedDeleteTarget) return;
    setFailedDeleting(true);
    try {
      await axios.post(
        getApiEndpoint('DELETE_FAILED_DOCUMENT'),
        { document_name: failedDeleteTarget.name },
        { withCredentials: true },
      );
      toast.success('Failed ingestion removed', {
        description: `${failedDeleteTarget.file_name || failedDeleteTarget.name} deleted. You can upload the file again.`,
      });
      setFailedDeleteTarget(null);
      loadFailedDocs();
    } catch (err: any) {
      toast.error('Failed to remove record', {
        description: err?.response?.data?.exception?.split('\n').pop() || 'Please try again.',
      });
    } finally {
      setFailedDeleting(false);
    }
  };

  const { searchQuery, fileTypeFilter, documentTypeFilter, minFileSize, maxFileSize, dateRange } = filters;

  // The input stays bound to `searchQuery` (instant typing); the fetch runs off
  // this debounced copy, which previously fired one POST per keystroke. It also
  // stops each keystroke from tearing down the 30s auto-refresh interval below.
  const debouncedSearch = useDebouncedValue(searchQuery, 400);

  useEffect(() => {
    loadData();
  }, []);

  // Clear selection when filters/search/page change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [debouncedSearch, fileTypeFilter, documentTypeFilter, minFileSize, maxFileSize, dateRange, currentPage]);

  useEffect(() => {
    loadDocuments();
  }, [debouncedSearch, fileTypeFilter, documentTypeFilter, minFileSize, maxFileSize, dateRange, currentPage, pageSize]);

  // Auto-refresh every 30 seconds when there are pending documents
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const interval = setInterval(() => {
      loadDocuments();
      loadQueueSummary();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefreshEnabled, debouncedSearch, fileTypeFilter, documentTypeFilter, minFileSize, maxFileSize, dateRange, currentPage]);

  // Listen for real-time events from Frappe
  useEffect(() => {
    const handleRealtimeEvent = (data: any) => {
      if (data?.type === 'document_processing' || data?.type === 'document_uploaded') {
        // A document has been processed or uploaded - refresh the queue
        loadDocuments();
        loadQueueSummary();
      }
    };

    // @ts-ignore - frappe.realtime is available in Frappe context
    if (typeof frappe !== 'undefined' && frappe.realtime) {
      frappe.realtime.on('orgain_brain_event', handleRealtimeEvent); 
    }

    return () => {
      // @ts-ignore
      if (typeof frappe !== 'undefined' && frappe.realtime) {
        frappe.realtime.off('orgain_brain_event', handleRealtimeEvent);
      }
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadDocuments(),
        loadStats(),
        loadQueueSummary(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    // Only the newest request may write state - a slow earlier response landing
    // late would otherwise show results for a query already moved on from.
    const requestId = ++latestRequestRef.current;
    try {
      const params: Record<string, any> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (fileTypeFilter && fileTypeFilter !== 'all') {
        params.file_type = fileTypeFilter;
      }
      if (documentTypeFilter && documentTypeFilter !== 'all') {
        params.document_type = documentTypeFilter;
      }
      if (minFileSize) {
        params.min_file_size = minFileSize;
      }
      if (maxFileSize) {
        params.max_file_size = maxFileSize;
      }
      if (dateRange?.from) {
        const d = dateRange.from;
        params.submitted_after = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
      }
      if (dateRange?.to) {
        const d = dateRange.to;
        params.submitted_till = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
      }

      const result = await validationService.getPendingValidations(params);
      if (requestId !== latestRequestRef.current) return; // superseded
      setDocuments(result.documents);
      setTotalDocuments(result.total);
    } catch (error: any) {
      if (requestId !== latestRequestRef.current) return; // superseded
      logger.error('Failed to load documents:', error);
      toast.error('Failed to load review queue');
    }
  };

  const loadStats = async () => {
    try {
      const data = await validationService.getValidationStats(7);
      setStats(data);
    } catch (error: any) {
      logger.error('Failed to load stats:', error);
    }
  };

  const loadQueueSummary = async () => {
    try {
      const data = await validationService.getQueueSummary();
      setQueueSummary(data);
    } catch (error: any) {
      logger.error('Failed to load queue summary:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Review queue refreshed');
  };

  const handleRowClick = (documentId: string) => {
    setSelectedDocumentId(documentId);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedDocumentId(null);
  };

  const handleModalSuccess = () => {
    loadData();
    setSelectedIds(new Set());
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(documents.map(d => d.document_id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (documentId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;

    setBulkApproving(true);
    try {
      const result = await validationService.bulkApprove(
        Array.from(selectedIds),
        'Bulk approved via Review Queue'
      );

      if (result.approved_count > 0) {
        toast.success(
          `${result.approved_count} document(s) approved and moved to production`
        );
      }
      if (result.failed_count > 0) {
        toast.error(
          `${result.failed_count} document(s) failed to approve`
        );
      }

      setSelectedIds(new Set());
      loadData();
    } catch (error: any) {
      logger.error('Bulk approval failed:', error);
      toast.error('Bulk approval failed');
    } finally {
      setBulkApproving(false);
    }
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    if (!sortColumn) return 0;

    if (sortColumn === 'entity_count') {
      const aVal = a.entity_count || 0;
      const bVal = b.entity_count || 0;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }

    if (sortColumn === 'submitted_at') {
      const aDate = new Date(a.submitted_at).getTime();
      const bDate = new Date(b.submitted_at).getTime();
      return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
    }

    return 0;
  });

  const getQueueStatusColor = (status?: string): string => {
    switch (status) {
      case 'normal': return 'text-emerald-400';
      case 'high': return 'text-amber-400';
      case 'critical': return 'text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  const getQueueStatusBadgeClasses = (status?: string): string => {
    switch (status) {
      case 'normal': return 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10';
      case 'high': return 'text-amber-400 border-amber-500/40 bg-amber-500/10';
      case 'critical': return 'text-red-400 border-red-500/40 bg-red-500/10';
      default: return 'text-muted-foreground border-border bg-muted';
    }
  };

  const isAllSelected = documents.length > 0 && selectedIds.size === documents.length;
  const totalPages = Math.ceil(totalDocuments / pageSize);

  // Removing the last row on the final page shrinks the list out from under
  // the current page, so clamp rather than render an empty one.
  const failedTotalPages = Math.max(1, Math.ceil(failedDocs.length / FAILED_PAGE_SIZE));
  const failedSafePage = Math.min(failedPage, failedTotalPages);
  const visibleFailedDocs = failedDocs.slice(
    (failedSafePage - 1) * FAILED_PAGE_SIZE,
    failedSafePage * FAILED_PAGE_SIZE,
  );

  useEffect(() => {
    if (failedPage !== failedSafePage) setFailedPage(failedSafePage);
  }, [failedPage, failedSafePage]);

  return (
    <div className="dark bg-background text-foreground min-h-full space-y-6 max-w-full p-6">
      {/* Header */}
      <KBPageHeader
        icon={<ClipboardList className="h-7 w-7" />}
        title="Review Queue"
        description="Review, approve, or reject documents that have been processed by the AI pipeline"
      />

      {/* Statistics Cards */}
      <StatsCardGrid cols={4}>
        <StatCardWithHeader
          title="Pending Review"
          value={
            <div className={`flex items-center gap-2 ${getQueueStatusColor(queueSummary?.queue_status)}`}>
              <FileText className="h-5 w-5" />
              <span>{queueSummary?.total_pending || stats?.total_pending || 0}</span>
              <span className={`text-xs font-medium uppercase border px-2 py-1 rounded-md ${getQueueStatusBadgeClasses(queueSummary?.queue_status)}`}>
                {queueSummary?.queue_status || 'Normal'}
              </span>
            </div>
          }
        />
        <StatCardWithHeader
          title="Approval Rate (7d)"
          value={stats?.approval_rate ? `${(stats.approval_rate * 100).toFixed(1)}%` : '0.0%'}
          valueColorClass={(stats?.approval_rate ?? 0) >= 0.8 ? 'text-emerald-400' : 'text-amber-400'}
        />
        <StatCardWithHeader
          title="Avg. Wait Time"
          value={queueSummary?.average_wait_time_minutes
            ? `${Math.round(queueSummary.average_wait_time_minutes)}m`
            : stats?.average_validation_time_minutes
              ? `${Math.round(stats.average_validation_time_minutes)}m`
              : '0.0 min'}
        />
        <StatCardWithHeader
          title="Approved (7d)"
          value={stats?.total_approved || 0}
          valueColorClass="text-emerald-400"
          icon={<CheckCircle2 className="h-5 w-5" />}
        />
      </StatsCardGrid>

      {/* Failed Ingestions - uploads that died before validation. Hidden
          when there are none. */}
      {failedDocs.length > 0 && (
        <Card className="border-red-500/30 bg-card/40">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <p className="text-sm font-semibold text-red-400">
                Failed Ingestions ({failedDocs.length})
              </p>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              These uploads failed before reaching review. Remove a record,
              then upload the file again.
            </p>
            {/* Capped height so a long failure list can't push the review
                queue off-screen; the page slice below keeps it to 10 rows. */}
            <div className="mt-4 max-h-[28rem] overflow-y-auto divide-y divide-border">
              {visibleFailedDocs.map((doc) => (
                <div
                  key={doc.name}
                  className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {doc.file_name || doc.title || doc.name}
                    </p>
                    <p className="mt-0.5 text-xs text-red-400/90 break-words">
                      {doc.error_summary}
                    </p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {doc.file_size ? `${formatFileSize(doc.file_size)} · ` : ''}
                      {doc.creation ? formatFullDate(doc.creation) : ''}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="sm:flex-shrink-0 border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-400"
                    onClick={() => setFailedDeleteTarget(doc)}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Remove
                  </Button>
                </div>
              ))}
            </div>

            {failedDocs.length > FAILED_PAGE_SIZE && (
              <DataTablePagination
                currentPage={failedSafePage}
                totalPages={failedTotalPages}
                totalItems={failedDocs.length}
                pageSize={FAILED_PAGE_SIZE}
                onPageChange={setFailedPage}
              />
            )}
          </CardContent>
        </Card>
      )}

      <ConfirmationDialog
        open={!!failedDeleteTarget}
        onOpenChange={(open) => { if (!open && !failedDeleting) setFailedDeleteTarget(null); }}
        onConfirm={handleDeleteFailed}
        title="Remove failed ingestion?"
        description={`This permanently deletes the failed record for "${failedDeleteTarget?.file_name || failedDeleteTarget?.name || ''}". The file itself is not in the knowledge base - you can upload it again afterwards.`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="destructive"
        contentClassName="dark bg-background text-foreground"
      />

      {/* Search and Filters */}
      <KBSearchAndFilters
        filters={filters}
        dateRangeLabel="Submitted Date Range"
        portalClassName="dark"
      />

      {/* Actions Bar */}
      <Card className="mb-4">
        <CardContent className="py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <div className="flex flex-col gap-2 sm:contents [&>button]:w-full sm:[&>button]:w-auto">
              <Button
                onClick={handleBulkApprove}
                disabled={bulkApproving || selectedIds.size === 0}
              >
                {bulkApproving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Approve Selected ({selectedIds.size})
              </Button>

              <RefreshButton onClick={handleRefresh} loading={refreshing} />
            </div>

            {selectedIds.size > 0 && (
              <span className="text-sm text-muted-foreground">
                {selectedIds.size} document(s) selected
              </span>
            )}

            {autoRefreshEnabled && documents.length > 0 && (
              <span className="text-xs text-muted-foreground sm:ml-auto flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Auto-refreshing every 30s
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents Table / Cards */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner message="Loading review queue..." fullScreen={false} />
            </div>
          ) : documents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mb-4" />
              <p className="text-lg font-medium">Review queue is empty</p>
              <p className="text-muted-foreground">All documents have been reviewed. Upload new documents to see them here.</p>
            </div>
          ) : isMobile ? (
            /* Mobile Card View */
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                  <span className="text-sm text-muted-foreground">Select All</span>
                </div>
                <span className="text-sm text-muted-foreground">{documents.length} documents</span>
              </div>

              <div className="divide-y">
                {sortedDocuments.map((doc) => (
                  <div
                    key={doc.document_id}
                    className="p-4 active:bg-muted/50 transition-colors"
                    onClick={() => handleRowClick(doc.document_id)}
                  >
                    <div className="flex items-start gap-3">
                      <div onClick={(e) => e.stopPropagation()} className="pt-1">
                        <Checkbox
                          checked={selectedIds.has(doc.document_id)}
                          onCheckedChange={(checked) =>
                            handleSelectRow(doc.document_id, checked as boolean)
                          }
                          aria-label={`Select ${doc.file_name}`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-primary truncate mb-2">
                          {doc.file_name.replace(/\.[^.]+$/, '')}
                        </h3>

                        <div className="flex flex-wrap gap-1.5 mb-2">
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs">
                            {doc.file_type.toUpperCase()}
                          </Badge>
                          {doc.document_type && (
                            <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-xs">
                              {doc.document_type}
                            </Badge>
                          )}
                          {doc.complexity && (
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                doc.complexity === 'simple' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                                doc.complexity === 'moderate' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                                doc.complexity === 'complex' ? 'bg-orange-500/10 text-orange-400 border-orange-500/30' :
                                'bg-red-500/10 text-red-400 border-red-500/30'
                              }`}
                            >
                              {doc.complexity.replace('_', ' ')}
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>{doc.entity_count || 0} entities</span>
                          <span>{formatRelativeTime(doc.submitted_at)}</span>
                        </div>

                        {doc.submitted_by && (
                          <div className="text-xs text-muted-foreground mt-1">
                            by {doc.submitted_by}
                          </div>
                        )}
                      </div>

                      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                    </div>
                  </div>
                ))}
              </div>

              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalDocuments}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                isMobile
              />
            </>
          ) : (
            /* Desktop Table View */
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted/30 sticky top-0 z-10">
                    <TableRow className="border-b">
                      <TableHead className="w-12 py-4">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all"
                        />
                      </TableHead>
                      <TableHead className="font-medium text-foreground py-4">Filename</TableHead>
                      <TableHead className="font-medium text-foreground py-4">Type</TableHead>
                      <TableHead className="font-medium text-foreground py-4">Size</TableHead>
                      <TableHead className="font-medium text-foreground py-4">Document Type</TableHead>
                      <TableHead className="font-medium text-foreground py-4">Complexity</TableHead>
                      <SortableTableHeader
                        column="entity_count"
                        label="Entities"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        column="submitted_at"
                        label="Submitted"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                      <TableHead className="font-medium text-foreground py-4">Submitted By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedDocuments.map((doc) => (
                      <TableRow
                        key={doc.document_id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(doc.document_id)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(doc.document_id)}
                            onCheckedChange={(checked) =>
                              handleSelectRow(doc.document_id, checked as boolean)
                            }
                            aria-label={`Select ${doc.file_name}`}
                          />
                        </TableCell>
                        <TableCell className="min-w-[200px] max-w-[300px]">
                          <span
                            className="font-medium truncate block text-primary hover:text-primary/80 cursor-pointer"
                            title={doc.file_name}
                          >
                            {doc.file_name.replace(/\.[^.]+$/, '')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                            {doc.file_type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                        <TableCell>
                          {doc.document_type ? (
                            <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30">
                              {doc.document_type}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {doc.complexity ? (
                            <Badge
                              variant="outline"
                              className={
                                doc.complexity === 'simple' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' :
                                doc.complexity === 'moderate' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30' :
                                doc.complexity === 'complex' ? 'bg-orange-500/10 text-orange-400 border-orange-500/30' :
                                'bg-red-500/10 text-red-400 border-red-500/30'
                              }
                            >
                              {doc.complexity.replace('_', ' ').toUpperCase()}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">N/A</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                            {doc.entity_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground cursor-default">
                                  {formatRelativeTime(doc.submitted_at)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="dark">
                                <p>{formatFullDate(doc.submitted_at)}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {doc.submitted_by || 'System'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <DataTablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalDocuments}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Document Detail Modal (approve/reject/view details) */}
      <DocumentDetailModal
        documentId={selectedDocumentId}
        visible={modalVisible}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default KBReviewQueuePage;
